import * as THREE from 'three';

// Scratch triangle corners, reused across every triangle of a sweep. This used
// to allocate three Vector3 per triangle (plus a Line3 and two more Vector3 per
// crossing), which on a full-resolution scan meant hundreds of thousands of
// short-lived objects per measurement and a GC pause the user reads as a freeze.
const _p1 = new THREE.Vector3();
const _p2 = new THREE.Vector3();
const _p3 = new THREE.Vector3();

function worldPositionAt(attr, i, mw, out) {
  out.set(attr.getX(i), attr.getY(i), attr.getZ(i));
  return out.applyMatrix4(mw);
}

// Where the plane cuts the segment a→b, given each end's signed distance.
// Both ends are known to sit on opposite sides, so the only guard needed is
// against the two distances being equal (a degenerate, zero-thickness case).
function crossingPoint(a, b, da, db) {
  const denom = da - db;
  if (!Number.isFinite(denom) || denom === 0) return null;
  const t = da / denom;
  return new THREE.Vector3(
    a.x + (b.x - a.x) * t,
    a.y + (b.y - a.y) * t,
    a.z + (b.z - a.z) * t,
  );
}

/**
 * Get intersection contour(s) of a mesh with a plane (world space).
 * Returns closed and open polylines. Use for circumference measurement.
 *
 * Reads indexed geometry through its index buffer. It used to call
 * `geometry.toNonIndexed()`, which built a full de-indexed copy of the socket
 * on every measurement and never disposed it.
 *
 * @param {THREE.Mesh} mesh
 * @param {THREE.Plane} plane - In world space
 * @returns {{ closedPolylines: THREE.Vector3[][], openPolylines: THREE.Vector3[][] }}
 */
export function getPlaneMeshContour(mesh, plane) {
  const geom = mesh?.geometry;
  const posAttr = geom?.attributes?.position;
  if (!posAttr) return { closedPolylines: [], openPolylines: [] };

  mesh.updateMatrixWorld(true);
  const index = geom.index ? geom.index.array : null;
  const matrixWorld = mesh.matrixWorld;
  const triCount = index ? index.length / 3 : Math.floor(posAttr.count / 3);

  const segments = [];

  for (let t = 0; t < triCount; t++) {
    const i0 = index ? index[t * 3 + 0] : t * 3 + 0;
    const i1 = index ? index[t * 3 + 1] : t * 3 + 1;
    const i2 = index ? index[t * 3 + 2] : t * 3 + 2;
    const p1 = worldPositionAt(posAttr, i0, matrixWorld, _p1);
    const p2 = worldPositionAt(posAttr, i1, matrixWorld, _p2);
    const p3 = worldPositionAt(posAttr, i2, matrixWorld, _p3);
    const d1 = plane.distanceToPoint(p1);
    const d2 = plane.distanceToPoint(p2);
    const d3 = plane.distanceToPoint(p3);

    // Same above/below split as before (>= 0 counts as above), just without
    // building the two arrays for every triangle in the mesh.
    const nAbove = (d1 >= 0 ? 1 : 0) + (d2 >= 0 ? 1 : 0) + (d3 >= 0 ? 1 : 0);
    if (nAbove === 0 || nAbove === 3) continue;

    // The lone corner on its own side, and the two it is cut away from.
    let lone; let loneD; let o1; let o1D; let o2; let o2D;
    if (nAbove === 1) {
      if (d1 >= 0)      { lone = p1; loneD = d1; o1 = p2; o1D = d2; o2 = p3; o2D = d3; }
      else if (d2 >= 0) { lone = p2; loneD = d2; o1 = p1; o1D = d1; o2 = p3; o2D = d3; }
      else              { lone = p3; loneD = d3; o1 = p1; o1D = d1; o2 = p2; o2D = d2; }
    } else {
      if (d1 < 0)      { lone = p1; loneD = d1; o1 = p2; o1D = d2; o2 = p3; o2D = d3; }
      else if (d2 < 0) { lone = p2; loneD = d2; o1 = p1; o1D = d1; o2 = p3; o2D = d3; }
      else             { lone = p3; loneD = d3; o1 = p1; o1D = d1; o2 = p2; o2D = d2; }
    }

    const c1 = crossingPoint(lone, o1, loneD, o1D);
    const c2 = crossingPoint(lone, o2, loneD, o2D);
    if (c1 && c2) segments.push([c1, c2]);
  }

  if (segments.length === 0) {
    return { closedPolylines: [], openPolylines: [] };
  }

  if (!geom.boundingSphere) geom.computeBoundingSphere();
  const radius = geom.boundingSphere?.radius ?? 1;
  const epsilon = Math.max(1e-6, 1e-5 * radius);

  function key(v) {
    return `${Math.round(v.x / epsilon)}_${Math.round(v.y / epsilon)}_${Math.round(v.z / epsilon)}`;
  }
  function same(a, b) {
    return a.distanceTo(b) < epsilon;
  }

  const keyToPoint = new Map();
  for (const [a, b] of segments) {
    const ka = key(a);
    const kb = key(b);
    if (!keyToPoint.has(ka)) keyToPoint.set(ka, a.clone());
    if (!keyToPoint.has(kb)) keyToPoint.set(kb, b.clone());
  }

  const adj = new Map();
  for (const [a, b] of segments) {
    const ka = key(a);
    const kb = key(b);
    if (ka === kb) continue;
    if (!adj.has(ka)) adj.set(ka, []);
    adj.get(ka).push(kb);
    if (!adj.has(kb)) adj.set(kb, []);
    adj.get(kb).push(ka);
  }

  const used = new Set();
  function edgeKey(ka, kb) {
    return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
  }

  const closedPolylines = [];
  const openPolylines = [];

  for (const [startKey] of adj) {
    const neighbors = adj.get(startKey) || [];
    for (const nextKey of neighbors) {
      const e = edgeKey(startKey, nextKey);
      if (used.has(e)) continue;
      const poly = [keyToPoint.get(startKey).clone()];
      used.add(e);
      let prev = startKey;
      let curr = nextKey;
      poly.push(keyToPoint.get(curr).clone());

      const safety = segments.length * 4;
      let steps = 0;
      while (steps++ < safety) {
        const nextNeighbors = (adj.get(curr) || []).filter((k) => k !== prev && !used.has(edgeKey(curr, k)));
        if (nextNeighbors.length === 0) {
          break;
        }
        const nk = nextNeighbors[0];
        used.add(edgeKey(curr, nk));
        poly.push(keyToPoint.get(nk).clone());
        prev = curr;
        curr = nk;
        if (curr === startKey) break;
      }

      if (poly.length >= 2) {
        const isClosed = same(poly[0], poly[poly.length - 1]);
        if (isClosed) {
          poly.pop();
          closedPolylines.push(poly);
        } else {
          openPolylines.push(poly);
        }
      }
    }
  }

  return { closedPolylines, openPolylines };
}

/**
 * Compute length of a closed polyline (circumference).
 * @param {THREE.Vector3[]} points
 * @returns {number}
 */
export function closedPolylineLength(points) {
  if (points.length < 2) return 0;
  let len = 0;
  for (let i = 0; i < points.length; i++) {
    const next = (i + 1) % points.length;
    len += points[i].distanceTo(points[next]);
  }
  return len;
}
